import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Sun, Moon, Monitor } from "lucide-react";

// Three-way theme: `light` and `dark` are explicit overrides; `system`
// follows the OS preference and updates live when the user toggles dark mode
// at the OS level. Persisted in localStorage so reloads don't flicker.

export type ThemePreference = "light" | "dark" | "system";

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: "light" | "dark";
  setPreference: (next: ThemePreference) => void;
}

const STORAGE_KEY = "etus-mindmap:theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  if (v === "light" || v === "dark" || v === "system") return v;
  return "system";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  // Hint to native form controls (scrollbar, default text inputs) about
  // which color scheme they should render in.
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<"light" | "dark">(() =>
    readStoredPreference() === "dark"
      ? "dark"
      : readStoredPreference() === "light"
      ? "light"
      : systemPrefersDark()
      ? "dark"
      : "light",
  );

  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setResolved(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference]);

  const setPreference = (next: ThemePreference) => {
    setPreferenceState(next);
    localStorage.setItem(STORAGE_KEY, next);
    if (next === "system") setResolved(systemPrefersDark() ? "dark" : "light");
    else setResolved(next);
  };

  return (
    <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

// Three-button toggle. Compact enough to live in a sidebar footer.
export function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  const opt = (value: ThemePreference, label: string, icon: ReactNode) => {
    const active = preference === value;
    return (
      <button
        key={value}
        onClick={() => setPreference(value)}
        title={label}
        aria-label={label}
        aria-pressed={active}
        className={[
          "flex h-7 w-7 items-center justify-center rounded transition-colors",
          active
            ? "bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200",
        ].join(" ")}
      >
        {icon}
      </button>
    );
  };

  return (
    <div className="inline-flex gap-0.5 rounded-md border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
      {opt("light", "Light theme", <Sun className="h-4 w-4" aria-hidden />)}
      {opt("system", "System theme", <Monitor className="h-4 w-4" aria-hidden />)}
      {opt("dark", "Dark theme", <Moon className="h-4 w-4" aria-hidden />)}
    </div>
  );
}
